import { Col, Row } from 'reactstrap';
import moment from 'moment/moment';
import CarWashStatusBadge from './CarWashStatusBadge';
import Icon from './Icon';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ICarWash } from '../models/CarWash';
import Button from './Button';
import useUser from '../hooks/useUser';
import { useApiPut } from '../hooks/useApi';
import { UserType } from '../models/User';
import useCompany from 'hooks/useCompany';
import ConfirmDialog from './ConfirmDialog';

type Props = {
  carWash: ICarWash;
  onEdit: (carWash: ICarWash) => void;
};
const CarWashRow = ({ carWash: carWashParam, onEdit }: Props) => {
  const company = useCompany();
  const [carWash, setCarWash] = useState<ICarWash>();
  const [confirmAction, setConfirmAction] = useState<
    'dispatch' | 'accept' | null
  >(null);
  const user = useUser();

  useEffect(() => {
    setCarWash(carWashParam);
  }, [carWashParam]);

  const { mutate, isPending } = useApiPut('/carwash', carWash?.id);

  const showDispatchButton = useMemo(() => {
    return (
      !carWash?.dispatched &&
      !carWash?.valet_accepted &&
      [UserType.companyAdmin, UserType.agent, UserType.admin].includes(
        user?.tipo!
      )
    );
  }, [carWash?.dispatched, carWash?.valet_accepted, user?.tipo]);

  const showAcceptButton = useMemo(
    () =>
      !carWash?.valet_accepted &&
      user?.tipo === UserType.valet &&
      carWash?.dispatched,
    [carWash?.dispatched, carWash?.valet_accepted, user?.tipo]
  );

  const handleMutate = useCallback(
    (data: any) => {
      // Filter out read-only fields to avoid serializer issues
      const writableFields = Object.keys(carWash || {}).reduce((acc, key) => {
        // Exclude read-only fields (those ending with _detail, data_cadastro, data_alteracao)
        if (
          !key.endsWith('_detail') &&
          key !== 'data_cadastro' &&
          key !== 'data_alteracao'
        ) {
          acc[key] = (carWash as any)[key];
        }
        return acc;
      }, {} as any);

      mutate(
        {
          ...writableFields,
          ...data,
          company: company?.id
        } as any,
        {
          onSuccess: ({ data: carWashUpdated }) => {
            setCarWash(carWashUpdated);
          },
          onError: (error: any) => {
            console.error('Error updating car wash:', error);
          }
        }
      );
    },
    [mutate, carWash, company]
  );

  const closeToPromTime =
    moment(carWash?.prom_time).diff(moment(), 'minutes') < 30 &&
    !carWash?.delivered;

  if (!carWash) return <Row />;

  return (
    <Row
      className="border-bottom py-3 d-flex align-items-center"
      onClick={() => onEdit(carWash)}
      role="button"
    >
      <Col xs={10} md={11}>
        <Row>
          <Col xs={6} md={3} lg={2}>
            <small className="text-secondary text-nowrap">
              Tag
              <br />
            </small>
            <span className="fw-bold">
              #{carWash.tag}
              {closeToPromTime && (
                <Icon icon={'warning-red'} local width={20} />
              )}
            </span>
          </Col>
          <Col xs={6} md={2} lg={2}>
            <small className="text-secondary text-nowrap">
              Vehicle
              <br />
            </small>
            <span
              className="fw-bold text-truncate d-inline-block"
              style={{ maxWidth: '120px' }}
              title={carWash.vehicle || '-'}
            >
              {carWash.vehicle || '-'}
            </span>
          </Col>
          <Col lg={2} className="d-none d-lg-block">
            <small className="text-secondary text-nowrap">
              Agent
              <br />
            </small>
            <span className="fw-bold">
              {`${carWash.writer_detail?.first_name} ${carWash.writer_detail?.last_name}`}
            </span>
          </Col>
          <Col lg={1} className="d-none d-lg-block">
            <small className="text-secondary text-nowrap">
              Priority
              <br />
            </small>
            <span className="fw-bold">
              {carWash.priority_detail?.item || '-'}
            </span>
          </Col>
          <Col xs={6} md={2} lg={2}>
            <small className="text-secondary text-nowrap">
              Prom. Time
              <br />
            </small>
            <span className="fw-bold">
              {moment(carWash.prom_time).format('hh:mm a')}
            </span>
          </Col>
          <Col xs={6} md={3} lg={2}>
            <small className="text-secondary text-nowrap">
              Valet
              <br />
            </small>
            <span className="fw-bold">
              {showDispatchButton ? (
                <Button
                  className="text-light"
                  color="info"
                  onClick={(e) => {
                    e.stopPropagation();
                    setConfirmAction('dispatch');
                  }}
                  loading={isPending}
                  size="sm"
                >
                  Dispatch
                </Button>
              ) : (
                carWash.user_valet_detail?.first_name ||
                (showAcceptButton ? '' : 'Valet requested')
              )}
              {showAcceptButton && (
                <Button
                  className="text-light"
                  color="info"
                  onClick={(e) => {
                    e.stopPropagation();
                    setConfirmAction('accept');
                  }}
                  loading={isPending}
                  size="sm"
                >
                  Accept
                </Button>
              )}
            </span>
          </Col>
          <Col xs={12} md={2} lg={1} className="mt-2 mt-md-0">
            <small className="text-secondary text-nowrap">
              Status
              <br />
            </small>
            <CarWashStatusBadge status={carWash.status} />
          </Col>
        </Row>
      </Col>
      <Col
        className="text-end text-primary d-flex align-items-center justify-content-end"
        xs={2}
        md={1}
      >
        <Icon icon={'arrow-right'} local width={40} />
      </Col>

      <ConfirmDialog
        open={confirmAction === 'dispatch'}
        onClose={() => setConfirmAction(null)}
        onConfirm={() => handleMutate({ dispatched: true })}
        title="Dispatch to Valet"
        message="Are you sure you want to dispatch this car wash to a valet?"
        confirmText="Dispatch"
        confirmColor="info"
        loading={isPending}
      />

      <ConfirmDialog
        open={confirmAction === 'accept'}
        onClose={() => setConfirmAction(null)}
        onConfirm={() => handleMutate({ valet_accepted: true })}
        title="Accept Car Wash"
        message="Are you sure you want to accept this car wash?"
        confirmText="Accept"
        confirmColor="info"
        loading={isPending}
      />
    </Row>
  );
};

export default CarWashRow;
